<template>
  <v-container>
    <v-autocomplete
      :items="items"
      class="search-bar"
      variant="outlined"
      item-props
      label=""
      placeholder="Search for Companies, Contacts or Events"
      menu-icon=""
      :hide-no-data="hideNoData"
      :list-props="{
        maxHeight: '685px',
        class: 'search-bar__list-item'
      }"
      :menu-props="{
        maxHeight: '685px'
      }"
      content-class="search-bar"
      clearable
      item-value="value"
      @click:clear="clearFilter"
      @update:search="debouncedSearch"
      @change="navigateTo"
      :custom-filter="customFilter"
    >
      <template v-if="!hideNoData" v-slot:no-data>
        <v-list-item>
          <v-list-item-title class="no-data regular-body-sm">No results for {{ lastSearch }}</v-list-item-title>
        </v-list-item>
      </template>
      <template v-slot:append-inner>
        <template v-if="!isLoading">
          <SvgFactory name="icon_search" class="custom-icon custom-icon__default" :width="24" />
          <SvgFactory name="icon_search" customColor="#FFFFFF" class="custom-icon custom-icon__focused" :width="24" />
        </template>
        <IconnectionsLoader v-if="isLoading" :size="24" />
      </template>
      <template v-slot:item="{ item }">
        <v-list-item @click="goToItem(item.props)" :class="{
          'custom-separator__space': item?.props?.title?.isSeparator,
          'custom-list__item cursor-pointer': !item?.props?.title?.customText && !item?.props?.title?.isSeparator,
          'cursor-default no-action': item?.props?.title?.customText || item?.props?.title?.isSeparator,
        }">
          <template v-if="item?.props?.title?.customText">
            <v-list-item-title class="list-header bold-caption-md">
              <div class="d-flex justify-space-between">
                <p>{{ formatLabel(item?.props?.title?.customText) }}</p>
                <p
                  v-if="item?.props?.title?.customText !== 'Contact'"
                  @click="seeAll(item.props)"
                  @keydown="seeAll(item.props)"
                  class="see-all underline"
                >
                  See all
                </p>
              </div>
            </v-list-item-title>
          </template>
          <template v-else>
            <v-list-item-title class="regular-overline-lg list-item">
              {{ item?.props?.title?.text }}
            </v-list-item-title>
          </template>
          <template v-if="item?.props?.title?.isSeparator">
            <hr class="custom-separator" />
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </v-container>
</template>

<script>
import { getAutoCompleteSearch } from '@common/services/autocomplete';
import SvgFactory from '@design_system_v2/atoms/SvgFactory.vue';
import { tracker } from '@services';
import { getCompanyTypeService } from '@common/services/users/getCompanyTypeService';
import COMPANY_TYPE from '@common/constants/companyTypes';
import IconnectionsLoader from '@design_system_v2/atoms/IconnectionsLoader.vue';

export default {
  components: {
    SvgFactory,
    IconnectionsLoader,
  },
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      items: [],
      isLoading: false,
      debounceTimeout: null,
      lastSearch: '',
    };
  },
  methods: {
    formatLabel(label) {
      if (label === 'Company') {
        return 'Companies';
      }
      if (label === 'Contact') {
        return 'Contacts';
      }
      if (label === 'Event') {
        return 'Events';
      }
      return '';
    },
    clearFilter() {
      this.searchQuery = '';
      this.debouncedSearch();
      this.searchAll();
    },
    customFilter(...args) {
      return args;
    },
    onSearch(query) {
      if (query) {
        this.isLoading = true;
        getAutoCompleteSearch(query).then((response) => {
          this.items = this.transformItems(response);
          this.isLoading = false;

          this.searchQuery = query;
          tracker('searchbar_type', {
            query,
            results: response,
          });
        });
      } else {
        this.searchQuery = query;
        this.items = [];
      }
    },
    navigateTo(link) {
      if (link) {
        this.$router.push(link);
      }
    },
    transformItems(items) {
      const transformedItems = [];
      let lastType = '';
      const typeCount = {};

      items.forEach((item) => {
        typeCount[item.type] = (typeCount[item.type] || 0) + 1;
      });

      items.forEach((item, index) => {
        if (item.type !== lastType) {
          if (index !== 0) {
            transformedItems.push({ isSeparator: true });
          }
          transformedItems.push({
            text: this.lastSearch,
            customText: item.type,
            isHeader: true,
            type: item.type,
          });
          lastType = item.type;
        }
        transformedItems.push({ ...item, isHeader: false });
      });
      return transformedItems;
    },
    debouncedSearch(query) {
      this.lastSearch = query;
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.onSearch(query);
      }, 300);
    },
    seeAll(payload) {
      if (payload.type === 'Company') {
        window.postMessage({
          name: 'iConnectionsEventBus',
          type: 'changeMFe',
          path: this.getSeeAllPath(payload?.text),
        }, '*');
      }
      if (payload.type === 'Event') {
        window.postMessage({
          name: 'iConnectionsEventBus',
          type: 'changeMFe',
          path: `/events?querySearch=${payload?.text}`,
        }, '*');
      }
    },
    goToItem(payload) {
      if (!payload?.title?.customText && !payload?.title?.isSeparator) {
        tracker('searchbar_go', {
          query: this.searchQuery,
          results: this.items,
          result_clicked: payload,
        });

        if (payload.type === 'Contact') {
          window.postMessage({
            name: 'iConnectionsEventBus',
            type: 'changeMFe',
            path: `/user/${payload?.contactId}`,
          });
        }
        if (payload.type === 'Company') {
          window.postMessage({
            name: 'iConnectionsEventBus',
            type: 'changeMFe',
            path: this.getCompanyPath(payload?.companyId),
          }, '*');
        }
        if (payload.type === 'Event') {
          window.postMessage({
            name: 'iConnectionsEventBus',
            type: 'changeMFe',
            path: `/event/details/${payload?.eventNameId}`,
          }, '*');
        }
      }
    },
    searchAll() {
      this.items = [];
    },
    getCompanyPath(companyId) {
      if (this.isAllocator) return `/funds/company/${companyId}`;
      if (this.isManager) return `/allocators/company/${companyId}`;
      return '';
    },
    getSeeAllPath(query) {
      const filters = encodeURIComponent(JSON.stringify([{ id: 'searchTerm', items: query }]));
      if (this.isAllocator) return `/list/funds?filters=${filters}`;
      if (this.isManager) return `/list/allocators?filters=${filters}`;
      return '';
    },
  },
  computed: {
    hideNoData() {
      return !this.searchQuery?.length && !this.items.length;
    },
    isManager() {
      return getCompanyTypeService(COMPANY_TYPE.Manager);
    },
    isAllocator() {
      return getCompanyTypeService(COMPANY_TYPE.Allocator);
    },
  },
};
</script>
<style scoped lang="scss">
@import "@foundation";
.custom-list__item {
  &:hover {
    background: #F1F5F9;
  }
}
.search-bar {
  max-height: 40px;
  padding: 0;
}
.list-header {
  font-weight: bold;
  color: #475569;
}
.see-all {
  cursor: pointer;
  color: #475569;
}
.custom-separator {
  border-color: #e2e8f0;
  border-top: 0;
  &__space {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
.custom-icon {
  background: #F1F5F9;
  border-radius: 4px;
  transform: rotate(90deg);
  &__clear {
    margin-top: 8px;
  }
}
.list-item {
color: map-get($neutral, 'neutral-500');
}
.no-data {
  color: #1E293B;
}
</style>
<style>
.no-action {
  &:hover {
    .v-list-item__overlay {
      background: white;
    }
  }
}
.search-bar {
  max-height: 40px;
  .v-input__control {
    .v-field {
      border-radius: 8px;
      .v-field__outline > div{
        border-color: #CBD5E1;
      }
      .v-field__field {
        max-height: 40px;
        .v-field__input input{
          font-size: 14px !important;
          margin-bottom: 14px;
        }
      }
    }
    .v-field:not(.v-field--focused) {
      .v-field__append-inner {
        .custom-icon__focused {
          display: none;
        }
        .custom-icon__default {
          display: block;
        }
      }
    }
    .v-field.v-field--focused {
      .v-field__outline > div {
        border-color: #9B71A9;
      }

      .v-field__append-inner {
        .custom-icon__default {
          display: none;
        }
        .custom-icon {
          background: #9B71A9;
        }
      }
    }

    height: 40px;
    .v-input__slot {
      height: 40px;
      .v-input__append-inner,
      .v-input__prepend-inner {
        height: 40px;
      }
      input {
        height: 40px !important;
        line-height: 40px !important;
      }
    }
  }
}
.v-autocomplete--active-menu {
  color: #0F172A;
}
</style>
